<template>
  <div class="container mt-5">
    <h2 class="text-center mb-4">Enter Correct Answers</h2>

    <div class="form-group">
      <label for="numRounds">Number of Rounds</label>
      <input
        type="number"
        v-model="numRounds"
        @input="initializeRounds"
        class="form-control"
        id="numRounds"
        min="1"
        required
      />
    </div>

    <div class="form-group">
      <label for="roundSelect">Select Round</label>
      <select
        v-model="currentRoundIndex"
        @change="onRoundSelect"
        class="form-control"
        id="roundSelect"
      >
        <option v-for="(round, index) in rounds" :key="index" :value="index">
          Round {{ round.roundNumber }}
        </option>
      </select>
    </div>

    <div v-if="currentRound">
      <h3>Round {{ currentRound.roundNumber }}</h3>

      <div class="form-group">
        <label for="roundType">Round Type</label>
        <select
          v-model="currentRound.roundType"
          @change="onRoundTypeChange(currentRoundIndex)"
          class="form-control"
          id="roundType"
          required
        >
          <option value="" disabled>Select a Round Type</option>
          <option v-for="type in roundTypes" :key="type.id" :value="type.id">
            {{ type.roundName }}
          </option>
        </select>
      </div>

      <div
        v-for="(question, questionIndex) in currentRound.questions"
        :key="questionIndex"
        class="form-group mt-3"
      >
        <div class="row">
          <div class="col-md-8">
            <label :for="'answer-' + currentRoundIndex + '-' + questionIndex">Answer
              {{ questionIndex + 1 }}</label>
            <div v-if="question.type === 'wishbone'">
              <input
                type="text"
                v-model="question.options[0]"
                :id="'answer-' + currentRoundIndex + '-' + questionIndex + '-0'"
                class="form-control mb-2"
                placeholder="Enter first option"
                required
              />
              <input
                type="text"
                v-model="question.options[1]"
                :id="'answer-' + currentRoundIndex + '-' + questionIndex + '-1'"
                class="form-control mb-2"
                placeholder="Enter second option"
                required
              />
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  :id="'correct-' + currentRoundIndex + '-' + questionIndex + '-0'"
                  :name="'correct-' + currentRoundIndex + '-' + questionIndex"
                  :value="0"
                  v-model="question.correctOptionIndex"
                >
                <label class="form-check-label" :for="'correct-' + currentRoundIndex + '-' + questionIndex + '-0'">
                  First option is correct
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  :id="'correct-' + currentRoundIndex + '-' + questionIndex + '-1'"
                  :name="'correct-' + currentRoundIndex + '-' + questionIndex"
                  :value="1"
                  v-model="question.correctOptionIndex"
                >
                <label class="form-check-label" :for="'correct-' + currentRoundIndex + '-' + questionIndex + '-1'">
                  Second option is correct
                </label>
              </div>
            </div>
            <input
              v-else
              type="text"
              v-model="question.answer"
              :id="'answer-' + currentRoundIndex + '-' + questionIndex"
              class="form-control"
              placeholder="Enter correct answer"
              required
            />
          </div>
          <div class="col-md-4">
            <label
              :for="'pointValue-' + currentRoundIndex + '-' + questionIndex">Point
              Value</label>
            <input
              type="number"
              v-model="question.pointValue"
              :id="'pointValue-' + currentRoundIndex + '-' + questionIndex"
              class="form-control"
              placeholder="Enter point value"
              min="0"
              required
            />
          </div>
        </div>
      </div>

      <div class="mt-4">
        <button @click="saveCurrentRound" class="btn btn-success">Save</button>
        <button
          @click="previousRound"
          :disabled="currentRoundIndex <= 0"
          class="btn btn-secondary ml-2"
        >
          Previous Round
        </button>
        <button
          @click="nextRound"
          :disabled="currentRoundIndex >= numRounds - 1"
          class="btn btn-secondary ml-2"
        >
          Next Round
        </button>
      </div>

      <div v-if="saveConfirmation" class="alert alert-success mt-3">
        {{ saveConfirmation }}
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch, computed } from "vue";
import { doc, getDoc, getDocs, collection, setDoc } from "firebase/firestore";
import { db } from "@/firebase";

export default {
  name: "EnterCorrectAnswers",
  props: {
    gameIds: {
      type: Array,
      required: true,
    },
    onBackToGames: {
      type: Function,
      required: true,
    },
  },
  setup(props) {
    const numRounds = ref(0);
    const rounds = ref([]);
    const roundTypes = ref([]);
    const currentRoundIndex = ref(0);
    const saveConfirmation = ref("");

    const currentRound = computed(() => {
      return rounds.value[currentRoundIndex.value] || {};
    });

    const fetchRoundTypes = async () => {
      try {
        const roundTypesSnapshot = await getDocs(collection(db, "roundTypes"));
        roundTypes.value = roundTypesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));
      } catch (error) {
        console.error("Error fetching round types:", error);
      }
    };

    const fetchGameData = async () => {
      try {
        const allRounds = [];
        const firstGameId = props.gameIds[0];
        const roundsCollectionRef = collection(db, "games", firstGameId, "rounds");
        const roundsSnapshot = await getDocs(roundsCollectionRef);

        if (!roundsSnapshot.empty) {
          roundsSnapshot.forEach(doc => {
            const roundData = doc.data();
            allRounds.push({ ...roundData, roundId: doc.id });
          });

          allRounds.sort((a, b) => a.roundNumber - b.roundNumber);
        } else {
          console.error(`No rounds data found for the game ID: ${firstGameId}.`);
        }

        numRounds.value = allRounds.length;
        rounds.value = allRounds;
        initializeRounds();
      } catch (error) {
        console.error("Error fetching game data:", error);
      }
    };

    const initializeRounds = () => {
      if (rounds.value.length < numRounds.value) {
        const newRounds = Array.from({ length: numRounds.value - rounds.value.length }, (_, index) => ({
          roundNumber: rounds.value.length + index + 1,
          roundType: "",
          questions: [],
        }));
        rounds.value = [...rounds.value, ...newRounds];
      }
      if (rounds.value.length > numRounds.value) {
        rounds.value = rounds.value.slice(0, numRounds.value);
      }
      currentRoundIndex.value = 0;
      onRoundTypeChange(currentRoundIndex.value); // Update round type when initializing
    };

    const onRoundTypeChange = (roundIndex) => {
      if (roundIndex < 0 || roundIndex >= rounds.value.length) return;
      const round = rounds.value[roundIndex];
      const selectedType = roundTypes.value.find(type => type.id === round.roundType);

      if (selectedType) {
        const numQuestions = selectedType.numCorrectAnswers;
        round.questions = Array.from({ length: numQuestions }).map((_, index) => {
          const existingQuestion = round.questions[index] || {};
          const answerType = selectedType.answerTypes[index]?.type || 'text'; // Add a default type

          if (answerType === 'wishbone') {
            return {
              type: 'wishbone',
              options: existingQuestion.options || ['', ''],
              correctOptionIndex: existingQuestion.correctOptionIndex || 0,
              pointValue: existingQuestion.pointValue || 0,
            };
          } else {
            return {
              type: answerType,
              answer: existingQuestion.answer || '',
              pointValue: existingQuestion.pointValue || 0,
            };
          }
        });
      }
    };

    const saveCurrentRound = async () => {
      try {
        const round = currentRound.value;

        if (!round) return;

        const selectedType = roundTypes.value.find(type => type.id === round.roundType);
        const roundData = {
          roundType: round.roundType,
          questions: round.questions.map(q => {
            if (q.type === 'wishbone') {
              return {
                type: q.type,
                options: q.options,
                correctOptionIndex: q.correctOptionIndex,
                pointValue: q.pointValue
              };
            }
            return {
              type: q.type,
              answer: q.answer,
              pointValue: q.pointValue
            };
          }),
          roundName: selectedType?.roundName || "",
          roundDescription: selectedType?.roundDescription || "",
          roundNumber: round.roundNumber,
        };

        // Save to all game IDs, not just the first one
        const savePromises = props.gameIds.map(gameId => {
          const roundDocId = `round${round.roundNumber}`;
          const roundRef = doc(db, "games", gameId, "rounds", roundDocId);
          return setDoc(roundRef, roundData, { merge: true });
        });

        await Promise.all(savePromises);

        saveConfirmation.value = `Round ${round.roundNumber} saved successfully to all games!`;
        setTimeout(() => {
          saveConfirmation.value = "";
        }, 3000);
      } catch (error) {
        console.error("Error saving round:", error);
        saveConfirmation.value = "There was an error saving the round. Please try again.";
        setTimeout(() => {
          saveConfirmation.value = "";
        }, 3000);
      }
    };

    const previousRound = () => {
      if (currentRoundIndex.value > 0) {
        saveCurrentRound();
        currentRoundIndex.value -= 1;
        onRoundTypeChange(currentRoundIndex.value);
      }
    };

    const nextRound = () => {
      if (currentRoundIndex.value < numRounds.value - 1) {
        saveCurrentRound();
        currentRoundIndex.value += 1;
        onRoundTypeChange(currentRoundIndex.value);
      }
    };

    const onRoundSelect = async () => {
      const roundIndex = currentRoundIndex.value;
      const round = rounds.value[roundIndex];

      if (round && round.roundId) {
        try {
          const roundDocRef = doc(db, "games", props.gameIds[0], "rounds", round.roundId);
          const roundDocSnapshot = await getDoc(roundDocRef);

          if (roundDocSnapshot.exists()) {
            const roundData = roundDocSnapshot.data();
            rounds.value[roundIndex] = { ...roundData, roundNumber: round.roundNumber };
            onRoundTypeChange(roundIndex);
          } else {
            console.error(`Round data not found for ID: ${round.roundId}`);
          }
        } catch (error) {
          console.error("Error fetching round data:", error);
        }
      }
    };

    watch(numRounds, (newValue, oldValue) => {
      if (newValue !== oldValue) {
        initializeRounds();
      }
    });

    onMounted(() => {
      fetchRoundTypes();
      fetchGameData();
    });

    return {
      numRounds,
      rounds,
      roundTypes,
      currentRoundIndex,
      saveConfirmation,
      fetchRoundTypes,
      fetchGameData,
      onRoundSelect,
      saveCurrentRound,
      previousRound,
      nextRound,
      currentRound,
      onRoundTypeChange
    };
  },
};
</script>




<style scoped>
/* Add your styles here */
</style>
